import React, { useCallback, useRef } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useMutation } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import commonMessages from '../../../../common/messages.tsx';
import type { AsyncExportItem } from '../../../../common/types.tsx';
import { ProgressFlag } from '../../../../common/ui/progress-flag/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../common/constants.tsx';
import { getStatusCodeGroup } from '../../../../common/utils.tsx';
import type { exportIssuesTaskProgressFlagCancelMutation } from './__generated__/exportIssuesTaskProgressFlagCancelMutation.graphql';
import type { exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues$key } from './__generated__/exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues.graphql';
import messages from './messages.tsx';

type Props = {
	exportIssuesTask: exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues$key | null;
	exportItem: AsyncExportItem;
	onDismissed: () => void;
};

/**
 * Only update progress if it is higher than the current progress, this prevents updating progress with a delayed backend event
 */
const useProgress = (progress: number) => {
	const currentProgress = useRef(0);

	if (progress > currentProgress.current) {
		currentProgress.current = progress;
	}

	return currentProgress.current;
};

export const ExportIssuesTaskProgressFlag = ({
	exportIssuesTask,
	exportItem,
	onDismissed,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();

	const exportIssuesTaskData =
		useFragment<exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues on JiraIssueExportEvent {
					... on JiraIssueExportTaskProgress {
						task {
							id
						}
						progress
					}
					... on JiraIssueExportTaskSubmitted {
						task {
							id
						}
					}
				}
			`,
			exportIssuesTask,
		);

	const [cancelExportIssueMutation] = useMutation<exportIssuesTaskProgressFlagCancelMutation>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			mutation exportIssuesTaskProgressFlagCancelMutation($cloudId: ID!, $taskId: String) {
				jira {
					requestCancelIssueExportTask(cloudId: $cloudId, taskId: $taskId)
						@optIn(to: "JiraAsyncExportIssuesCancellation") {
						... on JiraIssueExportTaskCancellationResult {
							success
							errors {
								message
								extensions {
									statusCode
									errorType
								}
							}
						}
					}
				}
			}
		`,
	);

	const handleOnDismissed = useCallback(() => {
		onDismissed();

		const cancelEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(cancelEvent, 'cancelJiraIssueExportTask', {
			taskId: exportIssuesTaskData?.task?.id,
		});

		if (exportIssuesTaskData?.task?.id) {
			cancelExportIssueMutation({
				variables: {
					cloudId,
					taskId: exportIssuesTaskData?.task?.id,
				},
				onCompleted: (data) => {
					const { errors } = data.jira?.requestCancelIssueExportTask || {};

					if (errors && errors.length > 0) {
						const error = errors[0];

						fireErrorAnalytics({
							event: createAnalyticsEvent({}), // forces contextual attributes be included
							meta: {
								id: 'cancelJiraIssueExportTaskExportFailed',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							sendToPrivacyUnsafeSplunk: true,
							error: new MutationError(error),
							attributes: {
								taskId: exportIssuesTaskData?.task?.id,
								statusCode: error.extensions?.statusCode,
								errorType: error.extensions?.errorType,
								statusCodeGroup: getStatusCodeGroup(error.extensions?.statusCode),
							},
						});
					} else {
						fireOperationalAnalytics(
							createAnalyticsEvent({}),
							'cancelJiraIssueExportTask success',
							{
								taskId: exportIssuesTaskData?.task?.id,
								requestCancellationSuccess: data.jira?.requestCancelIssueExportTask?.success,
							},
						);
					}
				},
				onError: (error) => {
					fireErrorAnalytics({
						event: createAnalyticsEvent({}), // forces contextual attributes be included
						meta: {
							id: 'cancelJiraIssueExportTaskExportFailed',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						sendToPrivacyUnsafeSplunk: true,
						error,
						attributes: {
							taskId: exportIssuesTaskData?.task?.id,
						},
					});
				},
			});
		}
	}, [
		cancelExportIssueMutation,
		cloudId,
		createAnalyticsEvent,
		exportIssuesTaskData?.task?.id,
		onDismissed,
	]);

	const id = exportIssuesTaskData?.task?.id || 'export-issues-task-progress-flag';

	const progress = useProgress(exportIssuesTaskData?.progress || 0);

	return (
		<ProgressFlag
			onDismissed={handleOnDismissed}
			title={formatMessage(messages.exportIssuesFlagProgressTitle, {
				exportItemLabel: exportItem.label,
			})}
			description={formatMessage(messages.exportIssuesFlagProgressDescription)}
			progress={progress}
			progressAriaLabel={formatMessage(messages.exportIssuesFlagProgressAriaLabel, {
				progress: progress * 100,
			})}
			actions={[
				{
					content: formatMessage(commonMessages.exportIssuesAlreadyInProgressCancelExportAction),
					onClick: handleOnDismissed,
				},
			]}
			id={id}
		/>
	);
};
